import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  setLessonCompleted,
  setLessonSaved,
  useAllLessonStates,
  useAllLessons,
  useCurrentUserProfile,
  type LessonRow,
  type UserLessonStateRow,
} from '@busy/api';
import { deliveryHint, localizeLesson, type LocalizedLesson } from '@busy/core';
import { useToast } from '@busy/ui';
import { useUI } from '../store/ui';
import { useT } from '../lib/i18n';
import { richText } from '../lib/richText';
import { DWELL_THRESHOLD_SEC, useDwellForLesson, useLessonDwell } from '../store/lessonDwell';
import { useLessonViewSession } from '../lib/useLessonViewSession';

// ────────────────────────────────────────────────────────────────────────────
// Bookshelf model
//
// The library is structured as 6 topic "books" + a 4-chip filter row that
// applies at both the bookshelf root AND inside each book. Same vocabulary,
// two scopes: one mental model.
//
//   Chips: All / To do / Saved / Completed
//     - All       — every lesson in scope (default)
//     - To do     — delivered + not completed (the actionable queue)
//     - Saved     — bookmarked
//     - Completed — done
//
//   At the bookshelf root:
//     filter = "all"   → show the 6 topic book covers (the default view)
//     filter ≠ "all"   → show a flat lesson list matching that filter,
//                         across every book
//
//   Inside a book (or with search):
//     filter narrows that book's list (or the search results)
//
// URL state:
//   /lessons                       — bookshelf, all
//   /lessons?filter=<chip>         — bookshelf with a non-All filter active
//   /lessons?book=<slug>           — that book opened (filter = All)
//   /lessons?book=<slug>&filter=<> — that book opened with chip active
//   /lessons?expand=<id>           — deep-link from Today's bonus / skip-
//                                     ahead flow: auto-navigate to the
//                                     lesson's book then expand it.
// ────────────────────────────────────────────────────────────────────────────

const CATEGORY_ORDER = [
  'Retention',
  'Skill',
  'Customer service',
  'Pricing',
  'Marketing',
  'Time management',
] as const;

type BookCategory = (typeof CATEGORY_ORDER)[number];

// Slugs for URL serialization. Kebab-cased so a `?book=customer-service`
// URL is human-scannable.
const CATEGORY_TO_SLUG: Record<BookCategory, string> = {
  Retention: 'retention',
  Skill: 'skill',
  'Customer service': 'customer-service',
  Pricing: 'pricing',
  Marketing: 'marketing',
  'Time management': 'time-management',
};
const SLUG_TO_CATEGORY: Record<string, BookCategory> = Object.fromEntries(
  (Object.entries(CATEGORY_TO_SLUG) as Array<[BookCategory, string]>).map(([cat, slug]) => [
    slug,
    cat,
  ]),
) as Record<string, BookCategory>;

type ChipFilter = 'all' | 'todo' | 'saved' | 'completed';
const VALID_FILTERS: readonly ChipFilter[] = ['all', 'todo', 'saved', 'completed'] as const;

// Maps the DB's Title-Case category strings to dictionary keys (cat.*).
// Anything unmapped falls through to the raw category string.
function categoryKey(category: string): string {
  switch (category.toLowerCase()) {
    case 'retention': return 'cat.retention';
    case 'skill': return 'cat.skill';
    case 'customer service': return 'cat.customerService';
    case 'time management': return 'cat.timeManagement';
    case 'pricing': return 'cat.pricing';
    case 'marketing': return 'cat.marketing';
    default: return '';
  }
}

// Per-category gradient classes — used for both the book covers on
// the shelf AND the lesson-row header (so a lesson row visually echoes
// the book it lives in).
const CATEGORY_THUMB: Record<string, { gradient: string; textOnDark: boolean }> = {
  Retention: {
    gradient: 'bg-gradient-to-br from-red-600 to-red-700',
    textOnDark: true,
  },
  Skill: {
    gradient: 'bg-gradient-to-br from-navy-800 to-navy-900',
    textOnDark: true,
  },
  'Customer service': {
    gradient: 'bg-gradient-to-br from-gold-500 to-gold-400',
    textOnDark: false, // navy text on gold
  },
  Pricing: {
    gradient: 'bg-gradient-to-br from-navy-800 to-red-700',
    textOnDark: true,
  },
  Marketing: {
    gradient: 'bg-gradient-to-br from-navy-700 to-navy-600',
    textOnDark: true,
  },
  'Time management': {
    gradient: 'bg-gradient-to-br from-navy-900 to-navy-700',
    textOnDark: true,
  },
};

export function Lessons() {
  const lessons = useAllLessons();
  const states = useAllLessonStates();
  const profile = useCurrentUserProfile();
  const t = useT();
  const language = useUI((s) => s.language);
  const goals = (profile.data?.goals ?? []) as string[];
  const [search, setSearch] = useState('');
  // Only one lesson expanded at a time — matches the "one dwell timer at a
  // time" rule so the visible state and the gating logic stay in sync.
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // URL drives which view is active. ?book=<slug> opens a book;
  // ?filter=<chip> activates a non-All filter. Both can be combined
  // (e.g. ?book=skill&filter=todo). ?expand=<id> is handled below.
  const [searchParams, setSearchParams] = useSearchParams();
  const bookSlug = searchParams.get('book');
  const filterParam = searchParams.get('filter');
  const activeBook: BookCategory | null = bookSlug
    ? SLUG_TO_CATEGORY[bookSlug] ?? null
    : null;
  const activeFilter: ChipFilter =
    filterParam && (VALID_FILTERS as readonly string[]).includes(filterParam)
      ? (filterParam as ChipFilter)
      : 'all';

  function openBook(category: BookCategory) {
    const sp = new URLSearchParams(searchParams);
    sp.set('book', CATEGORY_TO_SLUG[category]);
    // Persist the active filter when entering a book — if the user was
    // viewing "Saved" across all books and taps a topic, they probably
    // still want to see their saved lessons within that topic.
    setSearchParams(sp);
    setSearch('');
    setExpandedId(null);
  }
  function setFilter(next: ChipFilter) {
    const sp = new URLSearchParams(searchParams);
    if (next === 'all') sp.delete('filter');
    else sp.set('filter', next);
    setSearchParams(sp);
    setExpandedId(null);
  }
  function backToBookshelf() {
    const sp = new URLSearchParams(searchParams);
    sp.delete('book');
    setSearchParams(sp);
    setSearch('');
    setExpandedId(null);
  }

  const stateById = states.data ?? {};

  // Localize once at the top so search + render see the active language.
  // Category stays on the base row (translation is render-time via cat.*).
  const localized = useMemo(
    () => (lessons.data ?? []).map((l) => localizeLesson(l, language)),
    [lessons.data, language],
  );

  // Deep-link support: /lessons?expand=<lesson-id> auto-navigates to the
  // lesson's book + expands that lesson. Used by the bonus / skip-ahead
  // flows on Today. Cleared after the first read so a back/forward nav
  // doesn't keep re-firing it.
  useEffect(() => {
    const target = searchParams.get('expand');
    if (!target) return;
    const lesson = localized.find((l) => l.id === target);
    if (lesson) {
      const cat = lesson.category as BookCategory;
      const sp = new URLSearchParams(searchParams);
      sp.delete('expand');
      sp.delete('filter');
      sp.set('book', CATEGORY_TO_SLUG[cat]);
      setSearchParams(sp, { replace: true });
      setExpandedId(target);
    } else {
      // Lesson not in our catalog (or data not loaded yet); strip the
      // param so we don't keep retrying.
      const sp = new URLSearchParams(searchParams);
      sp.delete('expand');
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localized]);

  // Filter the lesson list down to whatever the current view dictates.
  // Order of operations: book scoping → chip filter → search → sort.
  const filtered = useMemo(() => {
    let list = localized;
    if (activeBook) {
      list = list.filter((l) => l.category === activeBook);
    }
    if (activeFilter === 'todo') {
      list = list.filter((l) => {
        const st = stateById[l.id];
        return st?.viewed_at != null && !st?.completed;
      });
    } else if (activeFilter === 'saved') {
      list = list.filter((l) => stateById[l.id]?.saved);
    } else if (activeFilter === 'completed') {
      list = list.filter((l) => stateById[l.id]?.completed);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((l) => {
        const hook = (l.hook ?? '').toLowerCase();
        return l.title.toLowerCase().includes(q) || hook.includes(q);
      });
    }
    // Sort: delivered lessons first (viewed_at not null), preserving
    // original order within each group. Lets the starter pack + any
    // drip-delivered content sit on top of the locked catalog.
    list = list.slice().sort((a, b) => {
      const aD = stateById[a.id]?.viewed_at ? 0 : 1;
      const bD = stateById[b.id]?.viewed_at ? 0 : 1;
      return aD - bD;
    });
    return list;
  }, [localized, activeBook, activeFilter, search, stateById]);

  // Per-chip counts — scoped to the current book when one is open, or
  // the whole library at the root. Drives the "(N)" badges so the user
  // sees if there's anything in a chip before tapping.
  const filterCounts = useMemo(() => {
    const scope = activeBook
      ? localized.filter((l) => l.category === activeBook)
      : localized;
    return {
      all: scope.length,
      todo: scope.filter((l) => {
        const st = stateById[l.id];
        return st?.viewed_at != null && !st?.completed;
      }).length,
      saved: scope.filter((l) => stateById[l.id]?.saved).length,
      completed: scope.filter((l) => stateById[l.id]?.completed).length,
    };
  }, [localized, activeBook, stateById]);

  // Per-book stats for the bookshelf tiles. Total/delivered/completed
  // counts roll up so each cover tells the barber what's inside without
  // having to open it.
  const bookStats = useMemo(() => {
    const stats: Record<string, { total: number; delivered: number; completed: number }> = {};
    for (const cat of CATEGORY_ORDER) {
      stats[cat] = { total: 0, delivered: 0, completed: 0 };
    }
    for (const l of localized) {
      const s = stats[l.category];
      if (!s) continue;
      s.total += 1;
      const st = stateById[l.id];
      if (st?.viewed_at) s.delivered += 1;
      if (st?.completed) s.completed += 1;
    }
    return stats;
  }, [localized, stateById]);

  function toggle(id: string) {
    setExpandedId((cur) => (cur === id ? null : id));
  }

  const totalCount = lessons.data?.length ?? 0;
  const subtitleKey = totalCount === 1 ? 'lessons.subtitle_one' : 'lessons.subtitle_other';
  const seedFile = 'supabase/migrations/0002_seed_lessons.sql';

  // True when we're on the default bookshelf root with no filter / no
  // book / no search active — i.e. the "show me the 6 book covers" view.
  // Any non-default chip OR a book entry OR active search switches to
  // a flat lesson list.
  const onBookshelfRoot =
    !activeBook && activeFilter === 'all' && !search.trim();

  // Header label for the current view — used in the page heading once
  // the user has drilled into a book (the chip filter doesn't change
  // the heading; it just narrows the list under it).
  const viewLabel = (() => {
    if (activeBook) {
      const key = categoryKey(activeBook);
      return key ? t(key) : activeBook;
    }
    return null;
  })();

  return (
    <div className="max-w-page mx-auto p-4 md:p-6 space-y-4 pb-24">
      <header>
        {/* Breadcrumb back-link — only shows when drilled into a book.
            Goes back to the bookshelf root, preserving the active chip
            filter (so leaving a topic doesn't lose your "Saved" view). */}
        {activeBook && (
          <button
            type="button"
            onClick={backToBookshelf}
            className="text-[12px] text-ink-500 hover:text-ink-700 inline-flex items-center gap-1 mb-1"
          >
            <ChevronLeftIcon className="w-3.5 h-3.5" />
            {t('lessons.book.back')}
          </button>
        )}
        <h1 className="font-display text-4xl md:text-[42px] text-navy-900 dark:text-gold-500 leading-[1.05]">
          {viewLabel ?? t('lessons.title')}
        </h1>
        <p className="text-[15px] text-ink-500 mt-1.5">
          {activeBook
            ? t('lessons.book.subtitle', {
                done: bookStats[activeBook]?.completed ?? 0,
                total: bookStats[activeBook]?.total ?? 0,
              })
            : t(subtitleKey, { count: totalCount })}
        </p>
      </header>

      {/* Search — visible everywhere. Searching from the bookshelf root
          collapses books into a flat result list (composes with the
          active chip filter); inside a book it narrows that book's
          list. */}
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
        <input
          type="search"
          placeholder={t('lessons.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full h-11 pl-9 pr-4 rounded-lg border border-line bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
        />
      </div>

      {/* Filter chips — All / To do / Saved / Completed. Same set at
          the bookshelf root AND inside each book; counts are scoped to
          whichever view you're in. */}
      <FilterChipRow
        active={activeFilter}
        counts={filterCounts}
        onPick={setFilter}
        t={t}
      />


      {(lessons.loading || states.loading) && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-32 rounded-xl bg-bg-alt animate-pulse" />
          ))}
        </div>
      )}

      {lessons.error && (
        <div className="rounded-lg border border-red-100 dark:border-red-600/40 bg-red-50 dark:bg-red-600/15 text-red-700 dark:text-red-300 p-3 text-sm">
          {t('lessons.errorLoad')} {lessons.error}
        </div>
      )}

      {lessons.data && lessons.data.length === 0 && (
        <div className="rounded-xl border border-dashed border-line p-8 text-center text-ink-500">
          <p className="text-sm font-semibold">{t('lessons.empty.title')}</p>
          <p className="text-xs mt-1">
            {t('lessons.empty.body', { file: seedFile })
              .split(seedFile)
              .map((chunk, i, arr) => (
                <span key={i}>
                  {chunk}
                  {i < arr.length - 1 && (
                    <code className="bg-bg-alt px-1 rounded">{seedFile}</code>
                  )}
                </span>
              ))}
          </p>
        </div>
      )}

      {/* Bookshelf grid — only renders at the root (no book / no filter /
          no search). Six topic covers. The cross-cutting "shelves" that
          used to live below have been replaced by the chip row above:
          tap "Saved" or "To do" to get the same cross-topic view. */}
      {onBookshelfRoot && lessons.data && lessons.data.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {CATEGORY_ORDER.map((cat) => {
            const stats = bookStats[cat] ?? { total: 0, delivered: 0, completed: 0 };
            const key = categoryKey(cat);
            return (
              <BookTile
                key={cat}
                category={cat}
                label={key ? t(key) : cat}
                total={stats.total}
                delivered={stats.delivered}
                completed={stats.completed}
                countLabel={t(
                  stats.total === 1 ? 'lessons.book.countOne' : 'lessons.book.countOther',
                  { n: stats.total, delivered: stats.delivered, completed: stats.completed },
                )}
                onClick={() => openBook(cat)}
              />
            );
          })}
        </div>
      )}

      {/* Flat lesson list — renders when a book is open OR when a non-
          All chip filter is active OR when search is active. */}
      {!onBookshelfRoot && lessons.data && lessons.data.length > 0 && filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-line p-6 text-center text-ink-500">
          <p className="text-sm">
            {search.trim() ? t('lessons.noMatches') : t('lessons.filter.empty')}
          </p>
        </div>
      )}

      {!onBookshelfRoot && filtered.length > 0 && (
        <ul className="space-y-3">
          {filtered.map((l) => (
            <LessonRowItem
              key={l.id}
              lesson={l}
              state={stateById[l.id]}
              expanded={expandedId === l.id}
              onToggle={() => toggle(l.id)}
              goals={goals}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Bookshelf — topic book covers (gradient) + cross-cutting shelves (outlined)
// ────────────────────────────────────────────────────────────────────────────

function BookTile({
  category,
  label,
  total,
  delivered,
  completed,
  countLabel,
  onClick,
}: {
  category: BookCategory;
  label: string;
  total: number;
  delivered: number;
  completed: number;
  countLabel: string;
  onClick: () => void;
}) {
  const thumb = CATEGORY_THUMB[category] ?? CATEGORY_THUMB.Skill;
  const onDarkText = thumb.textOnDark ? 'text-white' : 'text-navy-900';
  const onDarkSubtle = thumb.textOnDark ? 'text-white/85' : 'text-navy-900/80';
  const trackBg = thumb.textOnDark ? 'bg-white/15' : 'bg-navy-900/15';
  const fillBg = thumb.textOnDark ? 'bg-white/85' : 'bg-navy-900/80';
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative ${thumb.gradient} rounded-2xl px-4 py-5 text-left shadow-xs hover:-translate-y-0.5 hover:shadow-md transition-all`}
      aria-label={label}
    >
      <div className={`text-[10px] font-extrabold tracking-[0.18em] uppercase ${onDarkSubtle}`}>
        {countLabel}
      </div>
      <h3 className={`font-display text-[26px] leading-[1.05] mt-1.5 ${onDarkText}`}>
        {label}
      </h3>
      {/* Progress bar — visualizes completed/total. Stays subtle on the
          gradient so the title carries the visual weight. */}
      <div className="mt-4">
        <div className={`h-1.5 rounded-full overflow-hidden ${trackBg}`}>
          <div
            className={`h-full ${fillBg} transition-all`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className={`text-[10px] tabular-nums mt-1 ${onDarkSubtle}`}>
          {completed} / {total} · {delivered} delivered
        </div>
      </div>
    </button>
  );
}

// Chip filter row — All / To do / Saved / Completed. Same component
// renders at the bookshelf root AND inside each book; the counts are
// scoped to whichever view it's mounted in.
function FilterChipRow({
  active,
  counts,
  onPick,
  t,
}: {
  active: ChipFilter;
  counts: { all: number; todo: number; saved: number; completed: number };
  onPick: (next: ChipFilter) => void;
  t: (key: string) => string;
}) {
  const chips: Array<{ kind: ChipFilter; label: string; count: number }> = [
    { kind: 'all',       label: t('lessons.filter.all'),       count: counts.all },
    { kind: 'todo',      label: t('lessons.filter.todo'),      count: counts.todo },
    { kind: 'saved',     label: t('lessons.filter.saved'),     count: counts.saved },
    { kind: 'completed', label: t('lessons.filter.completed'), count: counts.completed },
  ];
  return (
    <div className="flex gap-2 flex-wrap">
      {chips.map((c) => {
        const isActive = c.kind === active;
        return (
          <button
            key={c.kind}
            type="button"
            onClick={() => onPick(c.kind)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
              isActive
                ? 'bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900'
                : 'bg-bg-alt text-ink-700 hover:bg-line'
            }`}
          >
            {c.label} ({c.count})
          </button>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Lesson row — unchanged from the pre-bookshelf design. Renders the same
// gradient header tile per lesson, expanded body, save/complete buttons.
// ────────────────────────────────────────────────────────────────────────────

function LessonRowItem({
  lesson,
  state,
  expanded,
  onToggle,
  goals,
}: {
  lesson: LocalizedLesson<LessonRow>;
  state: UserLessonStateRow | undefined;
  expanded: boolean;
  onToggle: () => void;
  goals: readonly string[];
}) {
  const toast = useToast();
  const t = useT();
  const plays = (lesson.plays ?? []) as Array<{ t: string; d: string }>;
  const isSaved = state?.saved ?? false;
  const isCompleted = state?.completed ?? false;
  const isDelivered = state?.viewed_at != null;
  const thumb = CATEGORY_THUMB[lesson.category] ?? CATEGORY_THUMB.Skill;
  const catKey = categoryKey(lesson.category);
  const catLabel = catKey ? t(catKey) : lesson.category;

  // Dwell timer — runs only while this lesson is the expanded one. Single
  // active timer across all cards (store enforces preemption).
  const { secs: dwellSec, ready: dwellReady } = useDwellForLesson(lesson.id);
  const dwellOpen = useLessonDwell((s) => s.open);
  const dwellClose = useLessonDwell((s) => s.close);
  useEffect(() => {
    if (expanded) dwellOpen(lesson.id);
    else dwellClose(lesson.id);
    return () => {
      // Component unmount (filter change, navigation) — pause timing.
      dwellClose(lesson.id);
    };
  }, [expanded, lesson.id, dwellOpen, dwellClose]);

  // Lesson view session — persists each (open, close) cycle to
  // lesson_view_sessions for engagement-depth analysis (migration 0069).
  useLessonViewSession(lesson.id, expanded);
  const completeReady = isCompleted || dwellReady;
  const completeBlockedReason = expanded
    ? t('lesson.complete.needDwell', { sec: Math.max(0, DWELL_THRESHOLD_SEC - dwellSec) })
    : t('lesson.complete.openFirst');

  async function handleSave(e: React.MouseEvent) {
    e.stopPropagation();
    const { error } = await setLessonSaved(lesson.id, !isSaved);
    if (error) toast(t('lesson.toast.errorBookmark', { message: error.message }), 'error');
    else toast(t(isSaved ? 'lesson.toast.removed' : 'lesson.toast.saved'), 'success');
  }

  async function handleComplete(e: React.MouseEvent) {
    e.stopPropagation();
    const { error } = await setLessonCompleted(lesson.id, !isCompleted);
    if (error) toast(t('lesson.toast.errorComplete', { message: error.message }), 'error');
    else
      toast(
        t(isCompleted ? 'lesson.toast.markedIncomplete' : 'lesson.toast.markedComplete'),
        'success',
      );
  }

  const onDarkText = thumb.textOnDark ? 'text-white' : 'text-navy-900';
  const onDarkSubtle = thumb.textOnDark ? 'text-white/85' : 'text-navy-900/80';

  return (
    <li
      className={`rounded-2xl bg-surface border overflow-hidden transition-all shadow-xs ${
        isCompleted ? 'border-green-300/60' : 'border-line'
      } ${!expanded ? 'hover:-translate-y-0.5 hover:shadow-md' : ''}`}
    >
      {/* Demo-style colored thumb — full-width header with category gradient */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left"
      >
        <div className={`relative ${thumb.gradient} px-4 py-4`}>
          {/* Saved bookmark flag — top right */}
          <button
            type="button"
            onClick={handleSave}
            aria-label={isSaved ? t('lesson.bookmark.remove') : t('lesson.bookmark.save')}
            className={`absolute top-3 right-3 w-8 h-8 rounded-lg grid place-items-center transition-colors ${
              isSaved
                ? 'bg-black/40 text-gold-400'
                : 'bg-white/15 hover:bg-white/25 ' + (thumb.textOnDark ? 'text-white/80' : 'text-navy-900/70')
            }`}
          >
            <BookmarkIcon className="w-3.5 h-3.5" filled={isSaved} />
          </button>

          <div className={`text-[11px] font-extrabold tracking-[0.14em] uppercase ${onDarkSubtle} pr-12`}>
            {catLabel}
            {lesson.high_impact && (
              <span className="ml-2 opacity-80">· {t('lessons.highImpactSuffix')}</span>
            )}
          </div>
          <h3 className={`font-display text-[21px] leading-[1.1] mt-1.5 pr-12 ${onDarkText}`}>
            {lesson.title}
          </h3>
        </div>

        {/* Footer meta row */}
        <div className="px-4 py-3 flex items-center justify-between text-[12px] text-ink-500">
          <span className="inline-flex items-center gap-2">
            {lesson.duration ?? '—'}
            {isCompleted && (
              <span className="inline-flex items-center gap-1 bg-green-600 text-white px-1.5 py-0.5 rounded text-[10px] font-extrabold tracking-wide uppercase">
                <CheckSmIcon className="w-2.5 h-2.5" />
                {t('lessons.done')}
              </span>
            )}
            {!isDelivered && (
              <span className="inline-flex items-center gap-1 bg-ink-500/15 text-ink-700 dark:text-ink-300 px-1.5 py-0.5 rounded text-[10px] font-extrabold tracking-wide uppercase">
                <LockIcon className="w-2.5 h-2.5" />
                {t('lessons.locked.tag')}
              </span>
            )}
          </span>
          <span className="inline-flex items-center gap-3">
            <span
              className="inline-flex gap-1"
              aria-label={t('lessons.difficultyAria', { n: lesson.difficulty })}
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${
                    i < lesson.difficulty ? 'bg-gold-500' : 'bg-ink-300'
                  }`}
                />
              ))}
            </span>
            <ChevronIcon
              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            />
          </span>
        </div>
      </button>

      {/* Expanded body — locked state for un-delivered lessons (drip model). */}
      {expanded && !isDelivered && (
        <LockedLessonBody lessonId={lesson.id} goals={goals} />
      )}
      {expanded && isDelivered && (
        <div className="px-5 pb-5 space-y-4 text-sm border-t border-line pt-4">
          {lesson.hook && (
            <p className="text-ink-700 leading-relaxed border-l-2 border-gold-500 pl-3">
              {richText(lesson.hook)}
            </p>
          )}
          {lesson.insight && <Section label={t('lesson.section.insight')}>{richText(lesson.insight)}</Section>}
          {plays.length > 0 && (
            <div>
              <SectionLabel>{t('lesson.section.play')}</SectionLabel>
              <ol className="space-y-1.5 list-decimal pl-4">
                {plays.map((p, i) => (
                  <li key={i} className="leading-relaxed">
                    <strong>{richText(p.t)}</strong>{' '}
                    <span className="text-ink-700">{richText(p.d)}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {lesson.why && <Section label={t('lesson.section.why')}>{richText(lesson.why)}</Section>}
          {lesson.expect && <Section label={t('lesson.section.expect')}>{richText(lesson.expect)}</Section>}

          <button
            type="button"
            onClick={handleComplete}
            disabled={!completeReady}
            className={`w-full h-10 rounded-lg font-bold text-sm inline-flex items-center justify-center gap-2 transition-colors ${
              isCompleted
                ? 'bg-green-600 text-white hover:bg-green-700'
                : completeReady
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-navy-900/35 text-white/85 cursor-not-allowed'
            }`}
          >
            <CheckSmIcon className="w-4 h-4" />
            {isCompleted
              ? t('lesson.completed')
              : completeReady
              ? t('lesson.markComplete')
              : completeBlockedReason}
          </button>
        </div>
      )}
    </li>
  );
}

function LockedLessonBody({
  lessonId,
  goals,
}: {
  lessonId: string;
  goals: readonly string[];
}) {
  const t = useT();
  const hint = deliveryHint(lessonId, goals);
  // For day-of-week / month hints we resolve human-readable name keys.
  const hintVars = { ...(hint.vars ?? {}) } as Record<string, string | number>;
  if (hintVars.dow != null) hintVars.day = t(`weekday.${hintVars.dow}`);
  if (hintVars.month != null) hintVars.monthName = t(`month.${hintVars.month}`);
  return (
    <div className="px-5 pb-5 pt-4 border-t border-line space-y-3 bg-bg-alt/40">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-navy-900/8 dark:bg-gold-500/15 grid place-items-center shrink-0 text-navy-900 dark:text-gold-400">
          <LockIcon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <strong className="block text-sm text-ink-900">{t('lessons.locked.title')}</strong>
          <p className="text-xs text-ink-500 leading-relaxed mt-0.5">
            {t(hint.key, hintVars)}
          </p>
        </div>
      </div>
      <p className="text-[11px] text-ink-500 leading-relaxed pl-12">
        {t('lessons.locked.body')}
      </p>
    </div>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-extrabold tracking-[0.16em] uppercase text-ink-500 mb-1">
      {children}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <p className="text-ink-700 leading-relaxed">{children}</p>
    </div>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function BookmarkIcon({ className, filled }: { className?: string; filled: boolean }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function CheckSmIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
